import type { CSSObject } from "@mui/material";
import type { DeviceMode } from "../../model/types";
import { byDevice } from "./byDevice";

export type CoverTextVariant =
  | "header1"
  | "header2"
  | "header3"
  | "subtitle1"
  | "subtitle2"
  | "subtitle3";

/**
 * Какие значения витрины берём в десктопном режиме. Рамка десктопа тянется на
 * всю ширину диалога (maxWidth 100%), поэтому её ширина совпадает с шириной
 * окна и настоящий брейкпоинт здесь работает: на экранах от lg витрина
 * рисовала бы более крупные размеры.
 */
export type DesktopTier = "md" | "lg";

// Копия витрины: site/src/shared/components/blocks/CoverBlock/CoverText.tsx.
// mobile - значение на брейкпоинте xs, md и lg - на одноимённых брейкпоинтах.
const FONT_SIZES: Record<
  CoverTextVariant,
  { mobile: string; md: string; lg: string }
> = {
  header1: { mobile: "3rem", md: "4rem", lg: "4.5rem" },
  header2: { mobile: "2.5rem", md: "3.5rem", lg: "4rem" },
  header3: { mobile: "2rem", md: "3rem", lg: "3.5rem" },
  subtitle1: { mobile: "1rem", md: "1.0625rem", lg: "1.3rem" },
  subtitle2: { mobile: "0.9rem", md: "1.1rem", lg: "1.2rem" },
  subtitle3: { mobile: "0.8rem", md: "1rem", lg: "1.1rem" },
};

export function isHeaderVariant(variant: CoverTextVariant): boolean {
  return variant.startsWith("header");
}

interface CoverTextStylesInput {
  variant: CoverTextVariant;
  device: DeviceMode;
  /** Ступень размеров десктопного режима, в мобильном не участвует. */
  desktopTier: DesktopTier;
  /** Вес из настроек обложки ("300".."700"), перебивает дефолтный. */
  weight?: string;
  /** theme.typography.h1.fontFamily темы платформы. */
  headingFontFamily: string;
}

// Тема платформы включает responsiveFontSizes для h1..h6, поэтому у
// заголовков уже есть свой fontSize под "@media (min-width: ...)" с
// селектором из одного класса. Обычный fontSize из getCoverTextStyles имел бы
// ту же специфичность и проигрывал бы теме на широких вьюпортах по порядку
// правил. Удваиваем класс через "&&" (специфичность 0,2,0): свой размер
// побеждает независимо от того, в каком порядке emotion напечатает правила,
// и без !important.
const DOUBLED_CLASS = "&&";

export function getCoverTextStyles({
  variant,
  device,
  desktopTier,
  weight,
  headingFontFamily,
}: CoverTextStylesInput): CSSObject {
  const sizes = FONT_SIZES[variant];
  const fontSize = byDevice(device, {
    mobile: sizes.mobile,
    desktop: sizes[desktopTier],
  });

  if (isHeaderVariant(variant)) {
    return {
      fontFamily: headingFontFamily,
      [DOUBLED_CLASS]: { fontSize },
      fontWeight: weight || 900,
      textTransform: "uppercase",
      letterSpacing: "-0.01em",
      lineHeight: 0.95,
    };
  }

  return {
    [DOUBLED_CLASS]: { fontSize },
    fontWeight: weight || 500,
    lineHeight: 1.5,
  };
}
